import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Grid, Button, Heading } from '@design-systems/canvas-ui'
import { Dialog, Divider } from '@enterprise-ui/canvas-ui-react'
import { logEvent, LogLevel } from '@praxis/component-logging'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'libphonenumber-js'
import ConnectionResult from './ConnectionResult'
import '../css/header.css'
import '../css/GuestDetails.css'
import '../css/common.css'
import {
  clearGuestFlowSession,
  loadCaptivePortalContext,
  clearCaptivePortalContext,
  buildSuccessUrl,
  loadGuestFlowSession,
  saveGuestFlowSession,
  saveCaptivePortalContext,
  isSafeRedirectUrl,
} from '../utils/tmAuthStorage'
import {
  GUEST_BLOCK_PATH,
  GUEST_RESEND_EMAIL_PATH,
  GUEST_SESSION_PATH,
  GUEST_SPONSOR_STATUS_PATH,
  GUEST_SUBMIT_PATH,
  GUEST_UPDATE_STATUS_PATH,
} from '../utils/apiRoutes'
import {
  completeGuestGrant,
  GUEST_OTP_TOKEN_HEADER,
  isSignedToken,
} from '../utils/guestFlowGrant'
import {
  detectSponsorStatusTampering,
  detectBlockResponseTampering,
  SPONSOR_STATUS_TAMPERED_MESSAGE,
} from '../utils/sponsorStatusIntegrity'

function normalizeMacAddress(macAddress) {
  return macAddress?.trim().toLowerCase() || null
}

// Sentinel used to skip the generic "service unreachable" error UI in
// submitClicked's catch when BlockUser already showed the tamper popup.
class BlockResponseTamperedError extends Error {
  constructor(reason) {
    super(`block response tampered: ${reason}`)
    this.name = 'BlockResponseTamperedError'
  }
}

const OTP_WINDOW_TOTAL_SECONDS = 2 * 60
const SPONSOR_WINDOW_TOTAL_SECONDS = 10 * 60
const EMAIL_RESEND_UNLOCK_SECONDS = 5 * 60
const MAX_EMAIL_RESEND_ATTEMPTS = 5
const MAX_OTP_ATTEMPTS = 3

const GuestDetails = () => {
  const location = useLocation()
  const terminalStateRef = useRef(false)
  // Redirect URL returned by the backend after it validated the post-OTP grant
  // token. This is the ONLY URL the guest flow is allowed to redirect to.
  const grantRedirectUrlRef = useRef(null)
  // Server-signed token from the sponsor-status response, issued only when the
  // backend's database says the sponsor approved. Required to open the OTP step
  // and sent with every OTP submission (the backend rejects codes without it).
  const otpTokenRef = useRef(null)

  const [emailCountdownSeconds, setEmailCountdownSeconds] = useState(
    SPONSOR_WINDOW_TOTAL_SECONDS,
  )
  const [otpCountdownSeconds, setOtpCountdownSeconds] = useState(null)

  const [phoneNumber, setPhoneNumber] = useState('')
  const [reqId, setReqId] = useState(null)
  const [name, setName] = useState('')
  const [emailPrefix, setEmailPrefix] = useState('')
  const [retrynum, setRetrynum] = useState(0)

  const [dialogblock, setDialogblock] = useState(false)
  const [blockedAt, setBlockedAt] = useState(null)
  const [dialogopen, setDialogopen] = useState(false)
  const [dialogemail, setDialogemail] = useState(false)

  const [dis, setDis] = useState(true)
  const [disPhone, setDisPhone] = useState(true)
  const [disEmail, setDisEmail] = useState(true)
  const [disCode, setDisCode] = useState(false)
  const [disresend, setDisResend] = useState(true)
  const [resendretry, setResendRetry] = useState(true)
  const [otp, setOtp] = useState(null)

  const [retry, setRetry] = useState(false)
  const [wrongNumber, setWrongNumber] = useState(false)
  const [cancelopen, setCancelopen] = useState(false)

  const [sponsorstatus, setSponsorstatus] = useState(null)
  const [checkstatus, setCheckstatus] = useState(null)
  const [pollErrorCount, setPollErrorCount] = useState(0)
  const [resendStatus, setResendStatus] = useState(null)
  const [resendCount, setResendCount] = useState(0)
  const [emailRateLimited, setEmailRateLimited] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState('in')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [showResult, setShowResult] = useState(false)
  const [resultType, setResultType] = useState('success')
  const [resultMessage, setResultMessage] = useState('')
  const [resultActionLabel, setResultActionLabel] = useState('')
  const [tamperDetected, setTamperDetected] = useState(false)

  const DOMAIN = '@target.com'
  const routeState = useMemo(() => location.state || {}, [location.state])
  const searchParams = new URLSearchParams(window.location.search)
  const storedPortalState = useMemo(() => loadCaptivePortalContext() || {}, [])

  const mac = normalizeMacAddress(
    searchParams.get('client_mac') ||
      searchParams.get('mac_address') ||
      routeState.mac ||
      storedPortalState.mac,
  )
  const successUrl =
    routeState.successUrl ||
    storedPortalState.successUrl ||
    buildSuccessUrl(
      routeState.baseGrantUrl || storedPortalState.baseGrantUrl,
      routeState.userContinueUrl || storedPortalState.userContinueUrl,
    ) ||
    null
  const validatedSuccessUrl = useMemo(() => {
    if (!successUrl) return null
    return isSafeRedirectUrl(successUrl) ? successUrl : null
  }, [successUrl])
  const baseGrantUrl =
    routeState.baseGrantUrl || storedPortalState.baseGrantUrl || null
  const userContinueUrl =
    routeState.userContinueUrl || storedPortalState.userContinueUrl || null
  const loc = routeState.loc || storedPortalState.loc
  const devicetype =
    routeState.deviceType ||
    storedPortalState.deviceType ||
    window.navigator.userAgent

  const emailminutes = Math.floor(emailCountdownSeconds / 60)
  const emailseconds = emailCountdownSeconds % 60
  const minutes =
    otpCountdownSeconds === null ? -1 : Math.floor(otpCountdownSeconds / 60)
  const seconds = otpCountdownSeconds === null ? 0 : otpCountdownSeconds % 60
  const remainingOtpAttempts = Math.max(MAX_OTP_ATTEMPTS - retrynum, 0)

  useEffect(() => {
    if (!mac || !validatedSuccessUrl) return

    const currentSession = loadGuestFlowSession({
      mac,
      loc,
      successUrl: validatedSuccessUrl,
    })

    if (
      !storedPortalState.mac ||
      normalizeMacAddress(storedPortalState.mac) !== mac
    ) {
      saveCaptivePortalContext({
        ...storedPortalState,
        ...routeState,
        mac,
        loc,
        baseGrantUrl,
        userContinueUrl,
        successUrl: validatedSuccessUrl,
        deviceType: devicetype,
      })
    }

    if (!currentSession) {
      clearGuestFlowSession()
      return
    }

    saveGuestFlowSession({
      ...currentSession,
      mac,
      loc,
      successUrl: validatedSuccessUrl,
    })
  }, [
    baseGrantUrl,
    devicetype,
    loc,
    mac,
    routeState,
    storedPortalState,
    userContinueUrl,
    validatedSuccessUrl,
  ])

  const getFullEmail = () => `${emailPrefix}${DOMAIN}`

  const stopSponsorWait = useCallback(() => {
    setCheckstatus(null)
  }, [])

  const stopOtpWait = useCallback(() => {
    setOtpCountdownSeconds(null)
  }, [])

  const showTerminalResult = useCallback(
    (type, message, actionLabel) => {
      terminalStateRef.current = true
      stopSponsorWait()
      stopOtpWait()
      setSponsorstatus(null)
      setDialogemail(false)
      setDialogopen(false)
      setCancelopen(false)
      setRetry(false)
      setOtp(null)
      setResultType(type)
      setResultMessage(message)
      setResultActionLabel(actionLabel)
      setShowResult(true)
    },
    [stopOtpWait, stopSponsorWait],
  )

  const ensureGuestFlowToken = useCallback(async () => {
    if (!mac) {
      throw new Error('Missing MAC address in guest flow context')
    }

    if (!validatedSuccessUrl) {
      throw new Error('Missing or unsafe captive portal success redirect')
    }

    const currentSession = loadGuestFlowSession({
      mac,
      loc,
      successUrl: validatedSuccessUrl,
    })

    if (currentSession?.token) {
      return currentSession.token
    }

    const response = await fetch(GUEST_SESSION_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        mac_address: mac,
        location: loc,
        device_type: devicetype,
        base_grant_url: baseGrantUrl,
        user_continue_url: userContinueUrl,
        success_url: validatedSuccessUrl,
      }),
    })

    if (!response.ok) {
      throw new Error(`guest session failed: ${response.status}`)
    }

    const payload = await response.json()
    const token = payload?.token || payload?.guest_flow_token

    if (!token) {
      throw new Error('Guest session response missing token')
    }

    saveGuestFlowSession({
      token,
      expiresAt: payload?.expires_at || null,
      mac,
      loc,
      successUrl: validatedSuccessUrl,
    })

    return token
  }, [baseGrantUrl, devicetype, loc, mac, userContinueUrl, validatedSuccessUrl])

  const fetchGuest = useCallback(
    async (url, method, body, allowRetry = true, extraHeaders = {}) => {
      const token = await ensureGuestFlowToken()

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'X-Guest-Flow-Token': token,
          ...extraHeaders,
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      })

      if (allowRetry && (response.status === 401 || response.status === 403)) {
        clearGuestFlowSession()
        return fetchGuest(url, method, body, false, extraHeaders)
      }

      return response
    },
    [ensureGuestFlowToken],
  )

  const resetGuestFlow = useCallback(() => {
    clearGuestFlowSession()
    terminalStateRef.current = false
    grantRedirectUrlRef.current = null
    otpTokenRef.current = null
    setEmailCountdownSeconds(SPONSOR_WINDOW_TOTAL_SECONDS)
    setOtpCountdownSeconds(null)

    setPhoneNumber('')
    setReqId(null)
    setName('')
    setEmailPrefix('')
    setRetrynum(0)

    setDialogblock(false)
    setBlockedAt(null)
    setDialogopen(false)
    setDialogemail(false)

    setDis(true)
    setDisPhone(true)
    setDisEmail(true)
    setDisCode(false)
    setDisResend(true)
    setResendRetry(true)
    setOtp(null)

    setRetry(false)
    setWrongNumber(false)
    setCancelopen(false)

    setSponsorstatus(null)
    setCheckstatus(null)
    setPollErrorCount(0)
    setResendStatus(null)
    setResendCount(0)
    setEmailRateLimited(false)
    setSelectedCountry('in')
    setIsSubmitting(false)

    setShowResult(false)
    setResultType('success')
    setResultMessage('')
    setResultActionLabel('')
    setTamperDetected(false)
  }, [])

  // Hard stop when the sponsor-status response looks edited: halt polling and
  // timers, drop every token held by this flow and show the warning popup.
  // The user can only start over from a clean form.
  const handleTamperDetected = useCallback(
    (reason, requestId) => {
      terminalStateRef.current = true
      otpTokenRef.current = null
      grantRedirectUrlRef.current = null
      clearGuestFlowSession()
      stopSponsorWait()
      stopOtpWait()
      setSponsorstatus(null)
      setDialogemail(false)
      setDialogopen(false)
      setRetry(false)
      setOtp(null)
      setTamperDetected(true)

      logEvent(
        {
          message: 'Sponsor status response tampering detected',
          reason,
          requestId,
          mac: `...:${mac?.split(':').pop() ?? 'unknown'}`,
        },
        { level: LogLevel.Warn },
      )
    },
    [mac, stopOtpWait, stopSponsorWait],
  )

  const handleApprove = useCallback(() => {
    if (terminalStateRef.current) return

    stopSponsorWait()
    setSponsorstatus('Approved')
    setDialogemail(false)
    setDialogopen(true)
    setRetry(false)
    setOtpCountdownSeconds(OTP_WINDOW_TOTAL_SECONDS)
  }, [stopSponsorWait])

  const handleDecline = useCallback(() => {
    showTerminalResult('error', 'Request cancelled.', 'Back to Guest Form')
  }, [showTerminalResult])

  const SponsorStatus = useCallback(
    (requestId) => {
      if (terminalStateRef.current) return

      fetchGuest(GUEST_SPONSOR_STATUS_PATH + requestId, 'GET')
        .then((response) => response.json())
        .then((responsedata) => {
          if (terminalStateRef.current) return
          setPollErrorCount(0)

          // Block progression if the response was edited (proxy / devtools).
          const tamperReason = detectSponsorStatusTampering({
            response: responsedata,
            requestId,
            mac,
          })
          if (tamperReason) {
            handleTamperDetected(tamperReason, requestId)
            return
          }

          if (responsedata['status'] === 'Approved') {
            const otpToken = responsedata['otp_token']
            if (!isSignedToken(otpToken)) {
              handleTamperDetected('Approved without otp_token', requestId)
              return
            }
            otpTokenRef.current = otpToken
            setSponsorstatus('Approved')
            handleApprove()
            return
          }

          setSponsorstatus(responsedata['status'])
          if (responsedata['status'] === 'Declined') {
            handleDecline()
          } else if (responsedata['status'] === 'Time-Out') {
            showTerminalResult(
              'error',
              "Didn't receive approval from sponsor TM. Please try again.",
              'Back to Guest Form',
            )
          }
        })
        .catch((err) => {
          setPollErrorCount((prev) => prev + 1)
          logEvent(
            { message: 'SponsorStatus poll error', error: err?.message },
            { level: LogLevel.Error },
          )
        })
    },
    [
      fetchGuest,
      handleApprove,
      handleDecline,
      handleTamperDetected,
      mac,
      showTerminalResult,
    ],
  )

  const UpdateStatus = useCallback(
    (id, otpvalue, timeoutType) => {
      if (terminalStateRef.current) return

      fetchGuest(
        GUEST_UPDATE_STATUS_PATH,
        'PUT',
        { id, otp: otpvalue },
        true,
        otpTokenRef.current
          ? { [GUEST_OTP_TOKEN_HEADER]: otpTokenRef.current }
          : {},
      )
        .then((request) => request.json())
        .then((response) => {
          const msg = response['status']

          if (msg === 'Approved') {
            // "Approved" alone is not trusted (the response can be tampered
            // with). Exchange the server-signed grant token with the backend; it
            // validates the token and returns the redirect URL to use.
            stopOtpWait()
            completeGuestGrant({ grantToken: response['grant_token'] })
              .then((redirectUrl) => {
                grantRedirectUrlRef.current = redirectUrl
                showTerminalResult('success', '', 'Done')
              })
              .catch((err) => {
                grantRedirectUrlRef.current = null
                logEvent(
                  { message: 'Guest grant failed', error: err?.message },
                  { level: LogLevel.Error },
                )
                showTerminalResult(
                  'error',
                  'Unable to verify your access. Please start again.',
                  'Back to Guest Form',
                )
              })
            return
          }

          if (msg === 'Wrongotp') {
            setRetrynum((prev) => prev + 1)
            setRetry(true)
            setOtpCountdownSeconds(OTP_WINDOW_TOTAL_SECONDS)
            return
          }

          if (msg === 'Blocked') {
            setRetry(false)
            setDisCode(true)
            setBlockedAt(new Date())
            showTerminalResult(
              'error',
              'You are blocked. Please try again after 30 minutes.',
              'OK',
            )
            return
          }

          if (msg === 'Rejected') {
            showTerminalResult(
              'error',
              'Request cancelled.',
              'Back to Guest Form',
            )
            return
          }

          if (msg === 'Time-Out') {
            const message =
              timeoutType === 'otp' || dialogopen
                ? 'OTP expired. Please start again.'
                : "Didn't receive approval from sponsor TM. Please try again."
            showTerminalResult('error', message, 'Back to Guest Form')
            return
          }

          if (msg === 'Pending') {
            // The backend refused the code because the sponsor hasn't approved
            // yet (e.g. an edited sponsor-status response opened the OTP box).
            showTerminalResult(
              'error',
              "Your sponsor hasn't approved this request yet. Please try again.",
              'Back to Guest Form',
            )
            return
          }

          showTerminalResult(
            'error',
            'Verification failed. Please try again.',
            'Back to Guest Form',
          )
        })
        .catch((err) => {
          logEvent(
            { message: 'UpdateStatus error', error: err?.message },
            { level: LogLevel.Error },
          )
        })
    },
    [dialogopen, fetchGuest, showTerminalResult, stopOtpWait],
  )

  function SubmitGuestData() {
    logEvent(
      { message: `guest submit mac=...:${mac?.split(':').pop() ?? 'unknown'}` },
      { level: LogLevel.Info },
    )

    return fetchGuest(GUEST_SUBMIT_PATH, 'POST', {
      name,
      mac_address: mac,
      phone_number: phoneNumber,
      sponsor_email: getFullEmail(),
      location: loc,
      device_type: devicetype,
    })
      .then((resp) => {
        if (resp.status === 403) {
          // Server-side block enforcement (the real gate — the pre-check at
          // GET /api/guest/block is only advisory and can be skipped).
          return { blocked: true }
        }
        if (!resp.ok) {
          throw new Error(`submit guest failed: ${resp.status}`)
        }
        return resp.json()
      })
      .then((response) => {
        if (response.blocked) {
          setDialogblock(true)
          return false
        }

        if (response['message'] === 'Invalid number') {
          HandleWrongNumber()
          return false
        }

        if (
          response['message']
            ?.toLowerCase()
            .includes('maximum number of attempts reached')
        ) {
          setEmailRateLimited(true)
          setDialogemail(false)
          setResultType('error')
          setResultMessage(
            'Maximum sponsor email attempts reached. Please try again later or use a different sponsor email.',
          )
          setResultActionLabel('Back to Guest Form')
          setShowResult(true)
          return false
        }

        setReqId(response['id'])
        return true
      })
      .catch((err) => {
        logEvent(
          { message: 'SubmitGuestData error', error: err?.message },
          { level: LogLevel.Error },
        )
        setResultType('error')
        setResultMessage(
          'Unable to reach guest Wi-Fi service. Please try again.',
        )
        setResultActionLabel('Back to Guest Form')
        setShowResult(true)
        return false
      })
  }

  function HandleWrongNumber() {
    setWrongNumber(true)
    setResultType('error')
    setResultMessage('Invalid phone number. Please check and try again.')
    setResultActionLabel('Back to Guest Form')
    setShowResult(true)
  }

  function ResendEmail() {
    if (resendCount >= MAX_EMAIL_RESEND_ATTEMPTS - 1) {
      setEmailRateLimited(true)
      return
    }

    fetchGuest(GUEST_RESEND_EMAIL_PATH, 'POST', {
      id: reqId,
    })
      .then((request) => {
        if (!request.ok) {
          throw new Error(`resend email failed: ${request.status}`)
        }
        return request.json()
      })
      .then(() => {
        setResendStatus('sent')
        setResendCount((prev) => prev + 1)
        setTimeout(() => setResendStatus(null), 4000)
      })
      .catch((err) => {
        setResendStatus('error')
        logEvent(
          { message: 'ResendEmail error', error: err?.message },
          { level: LogLevel.Error },
        )
        setTimeout(() => setResendStatus(null), 4000)
      })
  }

  function BlockUser(phone_number) {
    const params = new URLSearchParams({
      mac_address: mac,
      phone_number,
    })

    return fetchGuest(`${GUEST_BLOCK_PATH}?${params.toString()}`, 'GET')
      .then((request) => {
        if (!request.ok) {
          throw new Error(`block user failed: ${request.status}`)
        }
        return request.json()
      })
      .then((response) => {
        // Block progression if the response was edited (proxy / devtools):
        // e.g. flipping "message": true -> false to bypass a 30-minute block.
        const tamperReason = detectBlockResponseTampering({
          response,
          mac,
          phoneNumber: phone_number,
        })
        if (tamperReason) {
          handleTamperDetected(tamperReason, 'block')
          throw new BlockResponseTamperedError(tamperReason)
        }

        return response['message']
      })
  }

  const Timeout = useCallback(
    (timeoutType = 'email') => {
      if (!reqId || terminalStateRef.current) return

      stopSponsorWait()
      stopOtpWait()
      UpdateStatus(reqId, '', timeoutType)
    },
    [reqId, stopOtpWait, stopSponsorWait, UpdateStatus],
  )

  useEffect(() => {
    if (showResult || dialogopen || reqId === null || checkstatus === null) {
      return undefined
    }

    if (emailCountdownSeconds <= 0) {
      Timeout('email')
      return undefined
    }

    const timer = window.setTimeout(() => {
      setEmailCountdownSeconds((prev) => Math.max(prev - 1, 0))
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [
    checkstatus,
    dialogopen,
    emailCountdownSeconds,
    reqId,
    showResult,
    Timeout,
  ])

  useEffect(() => {
    if (showResult || otpCountdownSeconds === null) {
      return undefined
    }

    if (otpCountdownSeconds <= 0) {
      Timeout('otp')
      return undefined
    }

    const timer = window.setTimeout(() => {
      setOtpCountdownSeconds((prev) =>
        prev === null ? null : Math.max(prev - 1, 0),
      )
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [otpCountdownSeconds, showResult, Timeout])

  useEffect(() => {
    if (reqId === null || checkstatus === null || showResult) {
      return undefined
    }

    SponsorStatus(reqId)
    const poller = window.setInterval(() => {
      SponsorStatus(reqId)
    }, 1000)

    return () => window.clearInterval(poller)
  }, [checkstatus, reqId, showResult, SponsorStatus])

  useEffect(() => {
    const resendAllowed =
      checkstatus !== null &&
      resendretry === true &&
      emailCountdownSeconds <= EMAIL_RESEND_UNLOCK_SECONDS

    setDisResend(!resendAllowed)
  }, [checkstatus, emailCountdownSeconds, resendretry])

  const handleOtpChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
    setOtp(value)
  }

  const handlePhone = (value, countryData) => {
    setDis(true)
    setDisEmail(true)

    const phn = '+' + value
    setPhoneNumber(phn)

    if (countryData?.countryCode) {
      setSelectedCountry(countryData.countryCode.toLowerCase())
    }

    const isValidPhone = isValidPhoneNumber(phn)
    setDisEmail(!isValidPhone)

    if (name.length >= 3 && emailPrefix.length >= 3 && isValidPhone) {
      setDis(false)
    }
  }

  const handleName = (e) => {
    const sanitized = e.target.value.replace(/[^A-Za-z0-9_\- ]/g, '')

    setName(sanitized)

    if (sanitized.length >= 3) {
      setDisPhone(false)
    } else {
      setDisPhone(true)
      setDisEmail(true)
      setDis(true)
      return
    }

    const isValidPhone = isValidPhoneNumber(phoneNumber)
    if (isValidPhone) {
      setDisEmail(false)
    }

    if (sanitized.length >= 3 && emailPrefix.length >= 3 && isValidPhone) {
      setDis(false)
    } else {
      setDis(true)
    }
  }

  const handleEmail = (e) => {
    setDis(true)

    let value = e.target.value

    if (value.includes(DOMAIN)) {
      value = value.replace(DOMAIN, '')
    }

    const cursorPosition = e.target.selectionStart
    const originalLength = value.length

    value = value.replace(/[^A-Za-z0-9._-]/g, '')
    value = value.replace(/^[._-]+/, '')
    value = value.slice(0, 30)

    setEmailPrefix(value)

    const newLength = value.length
    const lengthDifference = originalLength - newLength
    const newCursorPosition = Math.max(0, cursorPosition - lengthDifference)

    setTimeout(() => {
      e.target.setSelectionRange(newCursorPosition, newCursorPosition)
    }, 0)

    const isValidPhone = isValidPhoneNumber(phoneNumber)
    if (value.length >= 3 && name.length >= 3 && isValidPhone) {
      setDis(false)
    }
  }

  const submitClicked = () => {
    if (isSubmitting || tamperDetected) return

    if (!isValidPhoneNumber(phoneNumber)) {
      HandleWrongNumber()
      return
    }

    if (!mac) {
      setResultType('error')
      setResultMessage(
        'Unable to detect device MAC address from the captive portal. Please reconnect to Wi-Fi and try again.',
      )
      setResultActionLabel('Back to Guest Form')
      setShowResult(true)
      return
    }

    if (!validatedSuccessUrl) {
      setResultType('error')
      setResultMessage(
        'Redirect target is not allowed. Please reconnect to Wi-Fi and try again.',
      )
      setResultActionLabel('Back to Guest Form')
      setShowResult(true)
      return
    }

    setIsSubmitting(true)
    BlockUser(phoneNumber)
      .then((isBlocked) => {
        if (isBlocked === true) {
          setDialogblock(true)
          return false
        }

        terminalStateRef.current = false
        setCheckstatus('check')
        setEmailCountdownSeconds(SPONSOR_WINDOW_TOTAL_SECONDS)
        setOtpCountdownSeconds(null)

        return SubmitGuestData()
      })
      .then((ok) => {
        if (ok) {
          setDialogemail(true)
        }
      })
      .catch((err) => {
        if (err instanceof BlockResponseTamperedError) {
          // handleTamperDetected already showed the warning popup.
          return
        }
        logEvent(
          { message: 'submitClicked error', error: err?.message },
          { level: LogLevel.Error },
        )
        setResultType('error')
        setResultMessage(
          'Unable to reach guest Wi-Fi service. Please try again.',
        )
        setResultActionLabel('Back to Guest Form')
        setShowResult(true)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleEmailPopup = () => {
    setDialogemail(false)
    setSponsorstatus(null)
    setReqId(null)
    setEmailPrefix('')
    setEmailCountdownSeconds(SPONSOR_WINDOW_TOTAL_SECONDS)
    setOtpCountdownSeconds(null)
    setDisResend(true)
    setResendRetry(true)
  }

  const handleClick = () => {
    UpdateStatus(reqId, otp, undefined)
  }

  const handleReject = () => {
    UpdateStatus(reqId, '0', undefined)
  }

  const handleResend = () => {
    setDisResend(true)
    ResendEmail()
    if (resendCount < MAX_EMAIL_RESEND_ATTEMPTS - 1) {
      terminalStateRef.current = false
      setCheckstatus('check')
      setEmailCountdownSeconds(SPONSOR_WINDOW_TOTAL_SECONDS)
    }
    setResendRetry(false)
  }

  const dialogblockClose = () => {
    resetGuestFlow()
  }

  const handleResultAction = (action) => {
    if (action === 'done') {
      // Only redirect to the URL the backend returned after validating the
      // grant token; never to the client-held captive-portal success URL.
      const redirectUrl = grantRedirectUrlRef.current
      if (!redirectUrl || !isSafeRedirectUrl(redirectUrl)) {
        setResultType('error')
        setResultMessage(
          'Unable to verify your access. Please reconnect to Wi-Fi and try again.',
        )
        setResultActionLabel('Back to Guest Form')
        setShowResult(true)
        return
      }

      grantRedirectUrlRef.current = null
      clearCaptivePortalContext()
      clearGuestFlowSession()
      window.location.replace(redirectUrl)
      return
    }

    resetGuestFlow()
  }

  if (showResult) {
    return (
      <ConnectionResult
        isSuccess={resultType === 'success'}
        flowType="guest"
        message={resultMessage}
        actionLabel={resultActionLabel}
        hidePrimaryAction={resultType === 'success'}
        autoActionDelayMs={resultType === 'success' ? 3000 : null}
        onDone={() => handleResultAction('done')}
        onRetry={() => handleResultAction('retry')}
      />
    )
  }

  return (
    <React.Fragment>
      {tamperDetected && (
        <div
          className="modal-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="tamper-modal-title"
        >
          <div className="modal-content block-user-modal">
            <h2 id="tamper-modal-title" className="block-user-modal__header">
              Suspicious Activity Detected
            </h2>

            <div className="block-user-modal__content">
              {SPONSOR_STATUS_TAMPERED_MESSAGE}
              <br />
              <br />
              Please close any proxy or browser tools and submit a new request.
            </div>

            <Button onClick={resetGuestFlow} className="btn-primary">
              Start Over
            </Button>
          </div>
        </div>
      )}

      {dialogblock && (
        <div className="modal-overlay">
          <div className="modal-content block-user-modal">
            <h2 className="block-user-modal__header">
              Access Temporarily Restricted
            </h2>

            <div className="block-user-modal__content">
              You are blocked for:
              <div className="block-user-modal__phone">{phoneNumber}</div>
              For security reasons, access has been temporarily restricted.
              <br />
              Blocked at{' '}
              {blockedAt
                ? blockedAt.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'this time'}
              . Please try again after 30 minutes from that time.
            </div>

            <Button onClick={dialogblockClose} className="btn-primary">
              OK
            </Button>
          </div>
        </div>
      )}

      <div className="guest-details-container">
        <div className="guest-details__form">
          <Grid.Container>
            <Grid.Item xs={12}>
              <span className="guest-details__header">Guest Details</span>
            </Grid.Item>
            <Grid.Item xs={12}>
              <Divider />
            </Grid.Item>
          </Grid.Container>

          <Grid.Container direction="column">
            <Grid.Item className="guest-details__field">
              <Heading className="guest-details__label">
                Enter your Name:
              </Heading>
              <input
                type="text"
                maxLength={25}
                value={name}
                placeholder="Limit 3-25 characters"
                onChange={handleName}
                className="guest-details__input"
              />
            </Grid.Item>

            <Grid.Item className="guest-details__field">
              <Heading className="guest-details__label">
                Enter your Phone Number:
              </Heading>
              <PhoneInput
                disabled={disPhone}
                country={selectedCountry || 'in'}
                value={phoneNumber}
                enableSearch
                onChange={handlePhone}
                rules={{ validate: isValidPhoneNumber }}
              />
            </Grid.Item>

            <Grid.Item className="guest-details__field">
              <Heading className="guest-details__label">
                Enter your sponsor's Target Email ID:
              </Heading>
              <input
                disabled={disEmail}
                type="text"
                value={emailPrefix + DOMAIN}
                onChange={handleEmail}
                onFocus={(e) => {
                  const prefixLength = emailPrefix.length
                  setTimeout(() => {
                    e.target.setSelectionRange(prefixLength, prefixLength)
                  }, 0)
                }}
                onClick={(e) => {
                  const prefixLength = emailPrefix.length
                  if (e.target.selectionStart > prefixLength) {
                    e.target.setSelectionRange(prefixLength, prefixLength)
                  }
                }}
                className="guest-details__email-input"
              />

              <div className="guest-details__note">
                *Ensure that you've entered the correct sponsor email ID before
                proceeding further*
              </div>
            </Grid.Item>

            <Grid.Item className="guest-details__field">
              <div className="guest-details__info">
                Target will send a one-time passcode (OTP) to the provided
                mobile number post sponsor approval. Message and data rates may
                apply.
              </div>
            </Grid.Item>

            <Grid.Item className="guest-details__field">
              <button
                disabled={dis || isSubmitting || tamperDetected}
                onClick={submitClicked}
                className="guest-details__submit-btn"
              >
                {isSubmitting ? 'Sending...' : 'Next'}
              </button>
            </Grid.Item>
          </Grid.Container>
        </div>

        {dialogemail && (
          <div className="modal-overlay">
            <div className="modal-content email-approval-modal">
              <h2 className="email-approval-modal__header">Sponsor Approval</h2>

              <div className="email-approval-modal__content">
                An Email has been sent to your sponsor mail{' '}
                <strong>{emailPrefix + DOMAIN}</strong> for approval.
              </div>

              <div>
                {sponsorstatus !== 'Approved' &&
                sponsorstatus !== 'Declined' ? (
                  <div className="email-approval-modal__timer">
                    Valid for {emailminutes}:
                    {emailseconds < 10 ? `0${emailseconds}` : emailseconds}
                  </div>
                ) : (
                  <h4>Your sponsor has {sponsorstatus} your request.</h4>
                )}
              </div>

              <div className="email-approval-modal__actions">
                <Button
                  disabled={disresend || emailRateLimited}
                  onClick={handleResend}
                  className="btn-secondary"
                >
                  Resend
                </Button>
                <Button onClick={handleEmailPopup} className="btn-primary">
                  Edit
                </Button>
              </div>

              {resendStatus === 'sent' && (
                <div className="resend-status resend-status--success">
                  ✓ Email resent successfully.
                </div>
              )}
              {resendStatus === 'error' && (
                <div className="resend-status resend-status--error">
                  Failed to resend email. Please try again.
                </div>
              )}

              {emailRateLimited && (
                <div className="resend-status resend-status--error">
                  Maximum sponsor email attempts reached. Please try again later
                  or use a different sponsor email.
                </div>
              )}

              {pollErrorCount >= 3 && (
                <div className="poll-error-warning">
                  Unable to reach the approval service. Please check your
                  connection. Retrying automatically…
                </div>
              )}
            </div>
          </div>
        )}

        {dialogopen && (
          <div className="modal-overlay">
            <div className="modal-content otp-modal">
              <h2 className="otp-modal__header">Verify Code</h2>

              {sponsorstatus === 'Approved' && (
                <div className="otp-modal__content">
                  Your sponsor has approved your request.
                </div>
              )}

              <div className="otp-modal__content">
                Enter the code sent to your mobile number {phoneNumber}.
              </div>

              <input
                disabled={disCode}
                maxLength={6}
                value={otp || ''}
                onChange={handleOtpChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && otp?.length === 6) {
                    handleClick()
                  }
                }}
                placeholder="Enter 6-digit OTP"
                className="otp-modal__input"
                autoFocus
              />

              {retry && <h4>Incorrect OTP!</h4>}

              <div className="otp-modal__timer">
                {retrynum >= 3 ? (
                  <span>Code Expired!</span>
                ) : minutes >= 0 ? (
                  <span>
                    Code valid for {minutes}:
                    {seconds < 10 ? `0${seconds}` : seconds}
                  </span>
                ) : (
                  <span>Waiting for OTP...</span>
                )}
              </div>

              <div>
                {remainingOtpAttempts > 1 ? (
                  <h4>You have {remainingOtpAttempts} attempts remaining.</h4>
                ) : (
                  <h4>
                    One more incorrect OTP will block this guest for 30 minutes.
                  </h4>
                )}
              </div>

              {wrongNumber && (
                <h4 style={{ color: 'red' }}>Invalid Phone Number</h4>
              )}

              <div className="otp-modal__actions">
                <Button
                  disabled={cancelopen}
                  onClick={handleReject}
                  className="btn-secondary"
                >
                  Cancel
                </Button>
                <Button
                  disabled={cancelopen || !otp || otp.length < 6}
                  onClick={handleClick}
                  className="btn-primary"
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        )}

        <Dialog
          headingText="RESTRICTED"
          bodyText="Not accessible outside Target Network"
          isVisible={false}
        />
      </div>
    </React.Fragment>
  )
}

export default GuestDetails
